import { useMemo, useRef, useState } from 'react';
import {
  ActionButton,
  FundItemStyle,
  FundsBody,
  FundsContainer,
  FundsHeader,
  FundsMoreActionsList,
  HeaderElements
} from './style';
import { FaSort } from 'react-icons/fa';
import { FaSortAlphaDown } from 'react-icons/fa';
import { FaSortAlphaUp } from 'react-icons/fa';
import MoreIcon from '../../../../../assets/icons/more.svg?react';
import { MdClose } from 'react-icons/md';
import { FaCheck } from 'react-icons/fa6';
import TrashIcon from '../../../../../assets/icons/trashPrimary.svg?react';
import EditIcon from '../../../../../assets/icons/editPrimary.svg?react';
import AddIcon from '../../../../../assets/icons/addPrimary.svg?react';
import CopyIcon from '../../../../../assets/icons/copy.svg?react';
import {
  useCreateWorkflowFund,
  useDeleteWorkflowFund,
  useUpdateWorkflowFund
} from '../../../../../services/workflow.services';
import { ClipLoader } from 'react-spinners';
import { Popover } from 'react-tiny-popover';
import { useSearchParams } from 'react-router-dom';

const sortTypes = {
  none: 'none',
  desc: 'desc',
  asc: 'asc'
} as const;

type SortType = (typeof sortTypes)[keyof typeof sortTypes];

interface FundItemData {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface FundItemProps {
  title: string;
  fundId: string;
  onEditFund: () => void;
  tooltipId?: string;
}

const FundItem = ({ title, fundId, onEditFund }: FundItemProps) => {
  const [copied, setCopied] = useState<boolean>(false);
  const updateFund = useUpdateWorkflowFund();
  const deleteFund = useDeleteWorkflowFund();

  const [isEdit, setIsEdit] = useState<boolean>(false);

  const [mutatedTitle, setMutatedTitle] = useState<string>(title);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleDelete = () => {
    deleteFund.mutate(fundId, {
      onSuccess: () => {
        setIsOpen(false);
        onEditFund();
      },
      onError: (err: any) => {
        console.log(err);
      }
    });
  };

  const handleCancel = () => {
    setIsEdit(false);
    setMutatedTitle(title);
  };

  const handleEditToggle = () => {
    setIsOpen(false);
    setIsEdit(prev => !prev);
  };

  const handleEdit = () => {
    if (title === mutatedTitle) return setIsEdit(false);

    updateFund.mutate(
      {
        fundId,
        data: {
          name: mutatedTitle
        }
      },
      {
        onSuccess: () => {
          setIsEdit(false);
          onEditFund();
        },
        onError: (err: any) => {
          console.log(err);
        }
      }
    );
  };

  const onCopy = () => {
    setCopied(true);
    navigator.clipboard.writeText(title);
    setTimeout(() => {
      setIsOpen(false);
    }, 1000);
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  };

  const toggle = () => setIsOpen(prev => !prev);

  return (
    <>
      <FundItemStyle data-tooltip-content={title} data-tooltip-id="ligth-tooltip">
        {isEdit ? (
          <input value={mutatedTitle} onChange={e => setMutatedTitle(e.target.value)} />
        ) : (
          <h5>{mutatedTitle}</h5>
        )}

        {isEdit ? (
          <div className="actions">
            <button className="toggler" onClick={handleEdit}>
              {updateFund.isLoading ? <ClipLoader size={14} /> : <FaCheck />}
            </button>

            <button onClick={handleCancel}>
              <MdClose />
            </button>
          </div>
        ) : (
          <Popover
            isOpen={isOpen}
            positions={['bottom', 'right']}
            onClickOutside={() => setIsOpen(false)}
            content={() => (
              <FundsMoreActionsList>
                <li>
                  <button onClick={handleEditToggle}>
                    <EditIcon />
                    <span>Edit</span>
                  </button>
                </li>
                <li>
                  <button className="delete" onClick={handleDelete}>
                    {deleteFund.isLoading ? <ClipLoader size={14} /> : <TrashIcon />}
                    <span>Delete</span>
                  </button>
                </li>
                <li>
                  <button onClick={onCopy}>
                    <CopyIcon />
                    <span>{copied ? 'Copied' : 'Copy'}</span>
                  </button>
                </li>
              </FundsMoreActionsList>
            )}
          >
            <button className="toggler" onClick={toggle}>
              <MoreIcon />
            </button>
          </Popover>
        )}
      </FundItemStyle>
    </>
  );
};

interface FundsBoxProps {
  title: string;
  items: FundItemData[];
  filterbyKey: string;
  workflowId: string;
  onEditFund: () => void;
  sheetId: string;
  fileId: string;
  onDelete: () => void;
  onDeleteFunds: () => void;
}

export default function FundsBox({
  title,
  items,
  filterbyKey,
  workflowId,
  onEditFund,
  sheetId,
  fileId,
  onDelete,
  onDeleteFunds
}: FundsBoxProps) {
  const [searchParams] = useSearchParams();
  const periodId = searchParams.get('periodId');
  const [sort, setSort] = useState<SortType>(sortTypes.none);
  const createFund = useCreateWorkflowFund({
    params: {
      period_id: periodId
    }
  });
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isCreate, setIsCreate] = useState<boolean>(false);
  const [newTitle, setNewTitle] = useState<string>('');
  const ref = useRef<HTMLDivElement>(null);
  const onSortChange = () => {
    if (sort === sortTypes.none) setSort(sortTypes.desc);
    if (sort === sortTypes.desc) setSort(sortTypes.asc);
    if (sort === sortTypes.asc) setSort(sortTypes.none);
  };

  const sortedItems = useMemo(() => {
    if (sort === sortTypes.none)
      return [...items].filter(item => item.name.toLowerCase().includes(filterbyKey.toLowerCase()));

    return [...items]
      .sort((a, b) =>
        sort === sortTypes.desc ? a?.name?.localeCompare(b?.name) : b?.name?.localeCompare(a?.name)
      )
      .filter(item => item.name.toLowerCase().includes(filterbyKey.toLowerCase()));
  }, [items, sort, filterbyKey]);

  const onCreate = (value: boolean) => {
    setIsCreate(value);
  };

  const handleAdd = () => {
    createFund.mutate(
      {
        name: newTitle,
        sheet_id: sheetId
      },
      {
        onSuccess: () => {
          onEditFund();
          onCreate(false);
        },
        onError: (err: any) => {
          console.log(err);
        }
      }
    );
  };

  const handleAddClick = () => {
    onCreate(true);
    setIsOpen(false);
  };

  const handleDeleteFunds = () => {
    onDeleteFunds();
    setIsOpen(false);
  };

  const handleDeleteTab = () => {
    onDelete();
    setIsOpen(false);
  };

  return (
    <FundsContainer key={sort}>
      <FundsHeader>
        <h4>Tab: {title}</h4>
        <HeaderElements>
          <button onClick={onSortChange}>
            {sort === sortTypes.none ? (
              <FaSort />
            ) : sort === sortTypes.desc ? (
              <FaSortAlphaDown />
            ) : (
              <FaSortAlphaUp />
            )}
          </button>

          <ActionButton ref={ref}>
            <Popover
              isOpen={isOpen}
              positions={['bottom', 'right']}
              onClickOutside={() => setIsOpen(false)}
              content={() => (
                <FundsMoreActionsList>
                  <li>
                    <button onClick={handleAddClick}>
                      <AddIcon />
                      <span>Add</span>
                    </button>
                  </li>
                  {items.length > 0 && (
                    <li>
                      <button className="delete" onClick={handleDeleteFunds}>
                        <TrashIcon />
                        <span>Delete all fund names</span>
                      </button>
                    </li>
                  )}
                  <li>
                    <button className="delete" onClick={handleDeleteTab}>
                      <TrashIcon />
                      <span>Delete tab</span>
                    </button>
                  </li>
                </FundsMoreActionsList>
              )}
            >
              <button className="toggler" onClick={() => setIsOpen(prev => !prev)}>
                <MoreIcon />
              </button>
            </Popover>
          </ActionButton>
        </HeaderElements>
      </FundsHeader>

      <FundsBody>
        <h4>Fund names: {sortedItems.length}</h4>

        <ul>
          {isCreate && (
            <FundItemStyle>
              <input value={newTitle} onChange={e => setNewTitle(e.target.value)} />

              <div className="actions">
                <button className="toggler" onClick={handleAdd}>
                  {createFund.isLoading ? <ClipLoader size={14} /> : <FaCheck />}
                </button>

                <button onClick={() => onCreate(false)}>
                  <MdClose />
                </button>
              </div>
            </FundItemStyle>
          )}
          {sortedItems.map((item, index) => (
            <FundItem
              title={item.name}
              key={item.id}
              fundId={item.id}
              onEditFund={onEditFund}
              tooltipId={`tooltip-${item.id}-${index}`}
            />
          ))}
        </ul>
      </FundsBody>
    </FundsContainer>
  );
}
